import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';
import { CrudListComponent } from './crud-list.component';
import { AlertService, CrudService } from '../_services';
import { GroupedListPopup, ListingGroup, UngroupedGroupId } from '../_models';

export abstract class GroupedListComponent<T extends { showThreeDotMenu?: boolean }, S> extends CrudListComponent {
  groupPopup = GroupedListPopup;
  groups: ListingGroup<T>[] = [];
  selectedGroupFilter: number = null;
  selectedStatusFilter: S = null;
  groupModalTitle = '';
  groupName = '';
  groupSubmitted = false;
  editingGroup: ListingGroup<T> = null;
  protected abstract groupLabel: string;
  protected abstract ungroupedName: string;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected crudService: CrudService,
    protected alertService: AlertService,
    protected groupService: CrudService,
    public ngxSmartModalService: NgxSmartModalService
  ) {
    super(router, location, route, crudService, alertService);
  }

  protected abstract loadGroups(): void;

  protected abstract getItemGroupId(item: T): number;

  abstract getItemStatus(item: T): S;

  protected showLoading() {
    this.loading = true;
  }

  protected hideLoading() {
    this.loading = false;
  }

  protected setGroups(categories: { id: string | number; name: string }[], items: T[]) {
    const groups: ListingGroup<T>[] = categories.map((category) => {
      const id = Number(category.id);

      return this.createGroup(id, category.name, items.filter((item) => this.getItemGroupId(item) === id));
    });
    const ungrouped = items.filter((item) => !groups.some((group) => group.id === this.getItemGroupId(item)));

    // Listed even when empty, so its "+" action stays reachable
    groups.push(this.createGroup(UngroupedGroupId, this.ungroupedName, ungrouped));

    items.forEach((item) => {
      item.showThreeDotMenu = false;
    });

    this.groups = groups;
    this.resetMissingGroupFilter();
  }

  // Keeps each card's collapsed state across reloads
  protected createGroup(id: number, name: string, items: T[]): ListingGroup<T> {
    const previous = this.groups.find((group) => group.id === id);

    return { id: id, name: name, expanded: previous ? previous.expanded : true, items: items };
  }

  // A renamed or removed group no longer matches the filter, which would hide every card
  protected resetMissingGroupFilter() {
    if (this.selectedGroupFilter !== null && !this.groups.some((group) => group.id === this.selectedGroupFilter)) {
      this.selectedGroupFilter = null;
    }
  }

  isUngrouped(group: ListingGroup<T>): boolean {
    return group.id === UngroupedGroupId;
  }

  addGroup() {
    this.editingGroup = null;
    this.groupModalTitle = `Add ${this.groupLabel}`;
    this.groupName = '';
    this.groupSubmitted = false;
    this.ngxSmartModalService.getModal(GroupedListPopup.Group).open();
  }

  editGroup(group: ListingGroup<T>) {
    this.editingGroup = group;
    this.groupModalTitle = `Edit ${this.groupLabel}`;
    this.groupName = group.name;
    this.groupSubmitted = false;
    this.ngxSmartModalService.getModal(GroupedListPopup.Group).open();
  }

  saveGroup() {
    this.groupSubmitted = true;
    const name = this.groupName ? this.groupName.trim() : '';

    if (name) {
      const isEdit = this.editingGroup !== null;
      const request = isEdit
        ? this.groupService.patch(this.editingGroup.id, { name: name })
        : this.groupService.create({ name: name });

      this.showLoading();
      request.pipe(takeUntil(this._destroy$)).subscribe(
        () => {
          this.alertService.success(`${this.groupLabel} ${isEdit ? 'renamed' : 'added'}.`);
          this.closeGroupModal();
          this.loadGroups();
        },
        (error) => {
          this.handleError(error);
          this.hideLoading();
        }
      );
    }
  }

  closeGroupModal() {
    this.ngxSmartModalService.getModal(GroupedListPopup.Group).close();
    this.groupSubmitted = false;
  }

  toggleGroup(group: ListingGroup<T>) {
    group.expanded = !group.expanded;
  }

  isGroupVisible(group: ListingGroup<T>): boolean {
    const matchesGroupFilter = this.selectedGroupFilter === null || group.id === this.selectedGroupFilter;
    const matchesStatusFilter = this.selectedStatusFilter === null || this.getVisibleItems(group).length > 0;

    return matchesGroupFilter && matchesStatusFilter;
  }

  getVisibleItems(group: ListingGroup<T>): T[] {
    return group.items.filter(
      (item) => this.selectedStatusFilter === null || this.getItemStatus(item) === this.selectedStatusFilter
    );
  }

  toggleThreeDotMenu(item: T): void {
    this.closeOtherMenus(item);
    item.showThreeDotMenu = !item.showThreeDotMenu;
  }

  closeOtherMenus(current: T): void {
    this.groups.forEach((group) => {
      group.items.forEach((item) => {
        if (item !== current) {
          item.showThreeDotMenu = false;
        }
      });
    });
  }

  closeThreeDotMenu(item: T) {
    item.showThreeDotMenu = false;
  }

  // One table per group, so each tbody only closes its own rows - otherwise the other
  // tables treat a click on this table's menu icon as an outside click and close it again
  closeMenu(group: ListingGroup<T>): void {
    group.items.forEach((item) => {
      item.showThreeDotMenu = false;
    });
  }
}
